import { api } from '../../lib/api.js';
import { BaseComponent, html, nothing } from '../../lib/lit.js';
import { disabledCategories } from '../../lib/notifications.js';

/**
 * <community-settings> — what you want emailed to you.
 *
 * Every notification shows up in the portal regardless; these switches only
 * decide what is also emailed. There is an all-off switch and one per
 * category, and they are independent: turning everything off and on again
 * restores each category as it was.
 *
 * Each change saves at once. A settings page with a Save button invites the
 * person who flicked a switch and left to believe it took.
 */
export class CommunitySettings extends BaseComponent {
    static properties = {
        _prefs: { state: true },
        _loading: { state: true },
        _saving: { state: true },
        _error: { state: true },
        _saved: { state: true },
    };

    constructor() {
        super();
        /** @type {{emailAvailable: boolean, emailEnabled: boolean, categories: Array<object>}|null} */
        this._prefs = null;
        this._loading = true;
        this._saving = false;
        this._error = '';
        this._saved = false;
    }

    connectedCallback() {
        super.connectedCallback();
        this._load();
    }

    async _load() {
        this._loading = true;
        this._error = '';
        try {
            this._prefs = await api.get('/me/notification-preferences');
        } catch (err) {
            this._error = String(/** @type {any} */ (err).message ?? err);
        } finally {
            this._loading = false;
        }
    }

    /** @param {object} next  the whole preferences object as it should now be */
    async _save(next) {
        const previous = this._prefs;
        this._prefs = next;
        this._saving = true;
        this._saved = false;
        this._error = '';
        try {
            this._prefs = await api.put('/me/notification-preferences', {
                emailEnabled: next.emailEnabled,
                disabledCategories: disabledCategories(next.categories),
            });
            this._saved = true;
        } catch (err) {
            this._prefs = previous;
            this._error = String(/** @type {any} */ (err).message ?? err);
        } finally {
            this._saving = false;
        }
    }

    _setEmailEnabled(enabled) {
        this._save({ ...this._prefs, emailEnabled: enabled });
    }

    _setCategory(category, email) {
        this._save({
            ...this._prefs,
            categories: this._prefs.categories.map((c) =>
                c.category === category ? { ...c, email } : c,
            ),
        });
    }

    _renderCategory(category) {
        const id = `email-${category.category}`;
        return html`
            <div class="form-check form-switch mb-2">
                <input
                    class="form-check-input"
                    type="checkbox"
                    role="switch"
                    id=${id}
                    .checked=${category.email}
                    ?disabled=${this._saving || category.transactional || !this._prefs.emailEnabled}
                    @change=${(e) => this._setCategory(category.category, e.target.checked)}
                />
                <label class="form-check-label" for=${id}>
                    ${category.label}
                    ${
                        category.transactional
                            ? html`<span class="text-muted small">(always sent)</span>`
                            : nothing
                    }
                </label>
            </div>
        `;
    }

    render() {
        if (this._loading) {
            return html`
                <div class="text-muted small d-flex align-items-center gap-2">
                    <span class="spinner-border spinner-border-sm" role="status"></span>
                    Loading…
                </div>
            `;
        }

        if (!this._prefs) {
            return html`<div class="alert alert-danger" role="alert">${this._error}</div>`;
        }

        return html`
            <h1 class="h4 mb-3">Email settings</h1>
            <p class="text-muted">
                Everything shows up under Notifications in the portal either way. These only decide
                what is also emailed to you.
            </p>
            ${
                this._prefs.emailAvailable
                    ? nothing
                    : html`<div class="alert alert-info small" role="status">
                          The portal isn't sending email yet. Your choices are saved for when it
                          does.
                      </div>`
            }
            ${this._error ? html`<div class="alert alert-danger" role="alert">${this._error}</div>` : nothing}

            <div class="card shadow-sm border-0 mb-3">
                <div class="card-body p-4">
                    <div class="form-check form-switch mb-3">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="email-enabled"
                            .checked=${this._prefs.emailEnabled}
                            ?disabled=${this._saving}
                            @change=${(e) => this._setEmailEnabled(e.target.checked)}
                        />
                        <label class="form-check-label fw-semibold" for="email-enabled">
                            Email me notifications
                        </label>
                    </div>
                    <div class="ms-4">
                        ${this._prefs.categories.map((c) => this._renderCategory(c))}
                    </div>
                </div>
            </div>
            ${
                this._saved && !this._saving
                    ? html`<div class="text-success small"><i class="bi bi-check2 me-1"></i>Saved</div>`
                    : nothing
            }
        `;
    }
}

customElements.define('community-settings', CommunitySettings);
